import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { Space, Button, Col, Row, Tag, Tooltip, Alert, Popover } from "antd";
import {
  ArrowLeftOutlined,
  DownloadOutlined,
  ReloadOutlined,
  EyeOutlined,
  TrophyOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import moment from "moment";
import Papa from "papaparse";
import { TABLE_ROWS_COUNT } from "../../shared/constants";
import { RootState } from "../../shared/types/common";
import DataTableComponent from "../../shared/components/data-table/data-table.component";
import { accessAllowed, showNotification } from "../../shared/utils/utils";
import { allPermissions } from "../../shared/constants/constants";
import { ROUTE_CANDIDATE_SCREENING } from "../../shared/routes/private.routes";
import {
  getJobDescriptionById,
  getScreeningResults,
  rescoreScreening,
} from "../../core/store/reducers/candidate-screening.slice";
import ScoreBreakdownComponent from "./components/score-breakdown.component";
import { AssessmentCaution } from "./models/candidate-screening.model";

const pct = (value?: number) => {
  if (value === null || value === undefined || isNaN(Number(value))) return "—";
  return `${Math.round(Number(value) * 100)}%`;
};

const safeText = (value: any) =>
  String(value ?? "")
    .replace(/[^\x09\x0A\x0D\x20-\x7E]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const safeFileName = (value: string) =>
  safeText(value || "candidate")
    .replace(/[^a-z0-9]+/gi, "_")
    .replace(/^_+|_+$/g, "")
    .toLowerCase();

const parseSkillRule = (rule: string) => {
  const match = String(rule || "").match(/skill\s+"(.+?)"\s+\(([\d.]+)\)/i);
  if (!match) return safeText(rule);
  return `${safeText(match[1])} (${Math.round(Number(match[2]) * 100)}% weight)`;
};

const formatAssessedOn = (value: any) =>
  value ? moment(value).format("DD-MMM-YYYY HH:mm") : "-";

const scoreComponents = (weightConfig: any) => ({
  similarity: Number(weightConfig?.similarity ?? 0) > 0,
  rule: Number(weightConfig?.rule ?? 0) > 0,
  ai: Number(weightConfig?.ai ?? 0) > 0,
});

const resultComponentEnabled = (
  record: any,
  component: "similarity" | "rule" | "ai"
) => {
  const weights = record?.breakdown?.effectiveWeights;
  if (!weights) return true;
  return Number(weights?.[component] ?? 0) > 0;
};

const scoreCell = (record: any, key: "similarityScore" | "ruleScore" | "aiScore") => {
  const component =
    key === "similarityScore" ? "similarity" : key === "ruleScore" ? "rule" : "ai";
  return resultComponentEnabled(record, component) ? pct(record?.[key]) : "-";
};

const cautionContent = (cautions: AssessmentCaution[]) => (
  <div style={{ width: 300, maxWidth: "calc(100vw - 48px)" }}>
    <div style={{ color: "#ad6800", fontWeight: 600, marginBottom: 8 }}>
      Assessment caution
    </div>
    {cautions.map((caution, index) => (
      <div key={caution.code || index} style={{ marginTop: index ? 8 : 0 }}>
        {caution.title && <div style={{ fontWeight: 600 }}>{caution.title}</div>}
        <div>{caution.message}</div>
      </div>
    ))}
  </div>
);

const wrapPdfText = (text: string, maxChars = 78) => {
  const words = safeText(text).split(" ").filter(Boolean);
  const lines: string[] = [];
  let line = "";

  words.forEach((word) => {
    const next = line ? `${line} ${word}` : word;
    if (next.length > maxChars && line) {
      lines.push(line);
      line = word;
    } else {
      line = next;
    }
  });

  if (line) lines.push(line);
  return lines.length ? lines : [""];
};

const escapePdfText = (text: string) =>
  safeText(text).replace(/\\/g, "\\\\").replace(/\(/g, "\\(").replace(/\)/g, "\\)");

type PdfLineStyle = "normal" | "section" | "metric" | "evidence" | "bullet";

interface PdfLine {
  text: string;
  style: PdfLineStyle;
}

const downloadTextPdf = (fileName: string, title: string, lines: string[]) => {
  const pageWidth = 595;
  const pageHeight = 842;
  const marginX = 46;
  const topY = 754;
  const bottomY = 48;
  const pages: PdfLine[][] = [[]];
  let y = topY;
  const sectionTitles = new Set([
    "Component Scores",
    "Similarity Match Explanation",
    "Rule Weight Score",
    "AI Score",
    "AI Rationale",
  ]);

  const lineStyle = (line: string): PdfLineStyle => {
    if (sectionTitles.has(line)) return "section";
    if (line.startsWith("Resume evidence:")) return "evidence";
    if (line.startsWith("- ")) return "bullet";
    if (
      line.startsWith("Similarity:") ||
      line.startsWith("Rule Weight Score:") ||
      line.startsWith("AI Score:") ||
      line.startsWith("Final Score:")
    ) {
      return "metric";
    }
    return "normal";
  };

  const lineHeight = (style: PdfLineStyle) => {
    if (style === "section") return 24;
    if (style === "evidence") return 13;
    return 16;
  };

  const maxChars = (style: PdfLineStyle) => {
    if (style === "evidence") return 92;
    if (style === "section") return 64;
    return 78;
  };

  const pushLine = (line: PdfLine) => {
    const height = lineHeight(line.style);
    if (y < bottomY) {
      pages.push([]);
      y = topY;
    }
    pages[pages.length - 1].push(line);
    y -= height;
  };

  lines.forEach((line) => {
    if (!line) {
      pushLine({ text: "", style: "normal" });
      return;
    }
    const style = lineStyle(line);
    wrapPdfText(line, maxChars(style)).forEach((wrappedLine, index) =>
      pushLine({
        text: wrappedLine,
        style: index === 0 ? style : style === "section" ? "normal" : style,
      })
    );
  });

  const objects: string[] = [];
  objects.push("<< /Type /Catalog /Pages 2 0 R >>");
  objects.push(
    `<< /Type /Pages /Kids [${pages
      .map((_page, index) => `${5 + index * 2} 0 R`)
      .join(" ")}] /Count ${pages.length} >>`
  );
  objects.push("<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica >>");
  objects.push("<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica-Bold >>");

  pages.forEach((pageLines, pageIndex) => {
    const pageObj = 5 + pageIndex * 2;
    const contentObj = pageObj + 1;
    objects.push(
      `<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${pageWidth} ${pageHeight}] /Resources << /Font << /F1 3 0 R /F2 4 0 R >> >> /Contents ${contentObj} 0 R >>`
    );

    let currentY = topY;
    const commands = [
      "0.36 0.30 0.92 rg",
      `0 792 ${pageWidth} 50 re f`,
      "BT /F2 18 Tf 1 1 1 rg",
      `${marginX} 813 Td (${escapePdfText(title)}) Tj`,
      "ET",
      `BT /F1 9 Tf 0.92 0.90 1 rg ${marginX} 799 Td (Generated candidate assessment report) Tj ET`,
    ];

    pageLines.forEach((line) => {
      if (!line.text) {
        currentY -= 10;
        return;
      }

      if (line.style === "section") {
        currentY -= 8;
        commands.push("0.86 0.88 0.92 RG");
        commands.push(`0.8 w ${marginX} ${currentY + 7} m ${pageWidth - marginX} ${currentY + 7} l S`);
        commands.push(
          `BT /F2 13 Tf 0.12 0.16 0.28 rg ${marginX} ${currentY - 8} Td (${escapePdfText(
            line.text
          )}) Tj ET`
        );
        currentY -= 24;
        return;
      }

      const font = line.style === "metric" || line.style === "bullet" ? "F2" : "F1";
      const size = line.style === "evidence" ? 9 : 11;
      const color =
        line.style === "metric"
          ? "0.05 0.25 0.70"
          : line.style === "evidence"
          ? "0.38 0.45 0.55"
          : "0.08 0.10 0.16";

      commands.push(
        `BT /${font} ${size} Tf ${color} rg ${marginX} ${currentY} Td (${escapePdfText(
          line.text
        )}) Tj ET`
      );
      currentY -= lineHeight(line.style);
    });

    const content = commands.join("\n");
    objects.push(`<< /Length ${content.length} >>\nstream\n${content}\nendstream`);
  });

  const pdfParts = ["%PDF-1.4\n"];
  const offsets = [0];
  objects.forEach((object, index) => {
    offsets.push(pdfParts.join("").length);
    pdfParts.push(`${index + 1} 0 obj\n${object}\nendobj\n`);
  });
  const xrefOffset = pdfParts.join("").length;
  pdfParts.push(`xref\n0 ${objects.length + 1}\n`);
  pdfParts.push("0000000000 65535 f \n");
  offsets.slice(1).forEach((offset) => {
    pdfParts.push(`${String(offset).padStart(10, "0")} 00000 n \n`);
  });
  pdfParts.push(
    `trailer\n<< /Size ${objects.length + 1} /Root 1 0 R >>\nstartxref\n${xrefOffset}\n%%EOF`
  );

  const blob = new Blob(pdfParts, { type: "application/pdf" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const ScreeningResultsPage: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { jobDescriptionId } = useParams();
  const { loadingResults, results, resultsPagination } = useSelector(
    (state: RootState) => state.candidateScreening
  );
  const { myPermissions } = useSelector((state: RootState) => state.auth);

  const [jd, setJd] = useState<any>(null);
  const [resultsData, setResultsData] = useState<any[]>([]);
  const [breakdownOpen, setBreakdownOpen] = useState(false);
  const [selectedResult, setSelectedResult] = useState<any>(null);
  const [isRescoring, setIsRescoring] = useState(false);
  const [scoreFilter, setScoreFilter] = useState<any>({
    scoreField: "finalScore",
    scoreOperator: "gte",
    scorePercent: undefined,
  });
  const enabledScores = useMemo(
    () => scoreComponents(jd?.weightConfig),
    [jd?.weightConfig]
  );
  const enabledScoreLabel = useMemo(
    () =>
      [
        enabledScores.similarity ? "similarity" : null,
        enabledScores.rule ? "rule" : null,
        enabledScores.ai ? "AI" : null,
      ]
        .filter(Boolean)
        .join(" + "),
    [enabledScores]
  );
  const hasAiSkippedResults = useMemo(
    () => resultsData.some((result: any) => result?.breakdown?.aiSkipped),
    [resultsData]
  );

  const loadResults = useCallback(
    (page: any = null, filterOverride?: any) => {
      if (!jobDescriptionId) return;
      const activeFilter = filterOverride ?? scoreFilter;
      const params = {
        page: page ? page : resultsPagination?.page ? resultsPagination?.page : 1,
        limit: resultsPagination?.limit || TABLE_ROWS_COUNT,
      };
      if (
        activeFilter?.scorePercent !== undefined &&
        activeFilter?.scorePercent !== null &&
        activeFilter?.scorePercent !== ""
      ) {
        Object.assign(params, {
          scoreField: activeFilter.scoreField,
          scoreOperator: activeFilter.scoreOperator,
          scorePercent: activeFilter.scorePercent,
        });
      }
      dispatch(getScreeningResults({ jobDescriptionId, params }));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [jobDescriptionId, dispatch, scoreFilter]
  );

  useEffect(() => {
    if (jobDescriptionId) {
      dispatch(getJobDescriptionById(jobDescriptionId)).then((val: any) => {
        setJd(val?.payload?.data?.data ?? val?.payload?.data ?? null);
      });
      loadResults();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobDescriptionId]);

  useEffect(() => {
    setResultsData(
      (results || []).map((obj: any, index: number) => ({
        key: obj?.id ?? index,
        ...obj,
      }))
    );
  }, [results]);

  const onPageChange = (pageNumber: number) => loadResults(pageNumber);

  const handleRescore = () => {
    if (!jobDescriptionId || isRescoring) return;
    setIsRescoring(true);
    dispatch(rescoreScreening({ jobDescriptionId }))
      .then((val: any) => {
        if (val?.payload?.data) {
          showNotification("Re-scoring completed.", "success");
          loadResults();
        }
      })
      .finally(() => {
        setIsRescoring(false);
      });
  };

  // Client-side CSV export of the ranked results currently loaded (plan.md §9).
  // Sensitive fields (e.g. CTC) are intentionally excluded from the export.
  const handleExport = useCallback(() => {
    if (!results || results.length === 0) {
      showNotification("No results to export.", "warning");
      return;
    }
    const rows = results.map((r: any) => {
      const row: any = {
        Rank: r?.rank ?? "",
        Candidate: r?.candidateName ?? "",
        Email: r?.candidateEmail ?? "",
      };
      row["Similarity %"] = scoreCell(r, "similarityScore");
      row["Rule %"] = scoreCell(r, "ruleScore");
      row["AI %"] = scoreCell(r, "aiScore");
      row["Final %"] = pct(r?.finalScore);
      row["Assessed On"] = formatAssessedOn(r?.assessedOn);
      row["Prompt Version"] = r?.promptVersion ?? "";
      return row;
    });
    const csv = Papa.unparse(rows);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    const safeTitle = (jd?.title || "job-description")
      .replace(/[^a-z0-9]+/gi, "_")
      .toLowerCase();
    link.setAttribute("download", `screening_results_${safeTitle}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, [results, jd, enabledScores]);

  const handleDownloadBreakdown = useCallback(
    (record: any) => {
      const breakdown = record?.breakdown || {};
      const similarityExplanation = breakdown?.similarityExplanation || {};
      const similarityTerms = similarityExplanation?.matchedKeywords || [];
      const lines: string[] = [
        `Job: ${jd?.title || "Results"}`,
        `Candidate: ${record?.candidateName || "-"}`,
        `Email: ${record?.candidateEmail || "-"}`,
        `Rank: ${record?.rank != null ? `#${record.rank}` : "-"}`,
        `Final Score: ${pct(record?.finalScore)}`,
        `Prompt Version: ${record?.promptVersion || "-"}`,
        `Scoring Strategy: ${
          breakdown?.scoringVersion === "v2"
            ? "V2 - Trust, then verify"
            : "V1 - Prove it"
        }`,
        ...(breakdown?.dataQuality === "LOW"
          ? [
              `Data Quality Warning: ${
                breakdown?.dataWarning ||
                "Very little readable text was found in this resume."
              }`,
            ]
          : []),
        "",
        "Component Scores",
      ];
      if (resultComponentEnabled(record, "similarity")) {
        lines.push(
          `Similarity: ${pct(record?.similarityScore ?? breakdown?.similarityScore)}`
        );
      } else {
        lines.push("Similarity: -");
      }
      if (resultComponentEnabled(record, "rule")) {
        lines.push(
          `Rule Weight Score: ${pct(record?.ruleScore ?? breakdown?.ruleScore)}`
        );
      } else {
        lines.push("Rule Weight Score: -");
      }
      if (resultComponentEnabled(record, "ai")) {
        lines.push(`AI Score: ${pct(record?.aiScore ?? breakdown?.aiScore)}`);
      } else {
        lines.push("AI Score: -");
      }

      if (
        resultComponentEnabled(record, "similarity") &&
        (similarityExplanation?.method || similarityTerms.length)
      ) {
        lines.push("", "Similarity Match Explanation");
        lines.push(
          similarityExplanation?.method ||
            "Similarity compares the full job description and resume. The terms below are visible resume/JD overlaps; primary and secondary skill weighting is shown separately."
        );
        if (similarityTerms.length) {
          similarityTerms.forEach((match: any) => {
            lines.push(`Term: ${safeText(match?.name)}`);
            if (match?.evidence) lines.push(`Resume evidence: ${match.evidence}`);
          });
        } else {
          lines.push(
            "No direct JD skill or keyword overlap was found in the readable resume text."
          );
        }
      }

      if (
        resultComponentEnabled(record, "rule") &&
        (breakdown?.matchedRules?.length || breakdown?.missedRules?.length)
      ) {
        lines.push("", "Rule Weight Score");
        if (breakdown?.matchedRules?.length) {
          lines.push("Matched:");
          breakdown.matchedRules.forEach((rule: string) =>
            lines.push(`- ${parseSkillRule(rule)}`)
          );
        }
        if (breakdown?.missedRules?.length) {
          lines.push("Missed:");
          breakdown.missedRules.forEach((rule: string) =>
            lines.push(`- ${parseSkillRule(rule)}`)
          );
        }
      }

      if (
        resultComponentEnabled(record, "ai") &&
        Array.isArray(breakdown?.skillEvidence) &&
        breakdown.skillEvidence.length
      ) {
        lines.push("", "AI Score");
        breakdown.skillEvidence.forEach((skill: any) => {
          lines.push(
            `- ${safeText(skill?.skill)} | ${
              skill?.tier === "must" ? "must-have" : "nice-to-have"
            } | ${skill?.present ? "on resume" : "not found"} | confidence ${Math.round(
              Number(skill?.confidence ?? 0)
            )}%`
          );
        });
      }

      if (breakdown?.aiRationale) {
        lines.push("", "AI Rationale", breakdown.aiRationale);
      }

      downloadTextPdf(
        `score_breakdown_${safeFileName(record?.candidateName || "candidate")}.pdf`,
        "Score Breakdown Report",
        lines
      );
    },
    [jd]
  );

  const rankTag = (rank?: number) => {
    if (rank == null) return "—";
    const color = rank === 1 ? "gold" : rank <= 3 ? "green" : "default";
    return <Tag color={color}>#{rank}</Tag>;
  };

  const columns = useMemo(() => {
    const visibleColumns: any[] = [
      {
        title: "Rank",
        dataIndex: "rank",
        key: "rank",
        render: (rank: number) => rankTag(rank),
      },
      { title: "Candidate", dataIndex: "candidateName", key: "candidateName" },
      { title: "Email", dataIndex: "candidateEmail", key: "candidateEmail" },
    ];

    visibleColumns.push(
      {
        title: "Similarity",
        dataIndex: "similarityScore",
        key: "similarityScore",
        render: (_v: number, record: any) => scoreCell(record, "similarityScore"),
      },
      {
        title: "Rule",
        dataIndex: "ruleScore",
        key: "ruleScore",
        render: (_v: number, record: any) => scoreCell(record, "ruleScore"),
      },
      {
        title: "AI",
        dataIndex: "aiScore",
        key: "aiScore",
        render: (_v: number, record: any) => scoreCell(record, "aiScore"),
      }
    );

    visibleColumns.push(
      {
        title: "Final",
        dataIndex: "finalScore",
        key: "finalScore",
        render: (v: number, record: any) => {
          const cautions: AssessmentCaution[] = record?.cautions || [];
          return (
            <Space size={6} wrap={false}>
              <span className="weight-600">{pct(v)}</span>
              {cautions.length > 0 && (
                <Popover
                  content={cautionContent(cautions)}
                  trigger={["hover", "click"]}
                  placement="top"
                >
                  <Button
                    type="text"
                    size="small"
                    aria-label="View assessment caution"
                    icon={<WarningOutlined style={{ color: "#d97706", fontSize: 17 }} />}
                    style={{ width: 26, height: 26, padding: 0 }}
                  />
                </Popover>
              )}
            </Space>
          );
        },
      },
      {
        title: "Assessed On",
        dataIndex: "assessedOn",
        key: "assessedOn",
        render: (value: any) => formatAssessedOn(value),
      },
      {
        title: "Actions",
        key: "actions",
        fixed: "right",
        render: (_text: any, record: any) => (
          <Space size={4}>
            <Tooltip title="View score breakdown">
              <Button
                type="link"
                icon={<EyeOutlined />}
                onClick={() => {
                  setSelectedResult(record);
                  setBreakdownOpen(true);
                }}
              />
            </Tooltip>
            <Tooltip title="Download score breakdown PDF">
              <Button
                type="link"
                icon={<DownloadOutlined />}
                onClick={() => handleDownloadBreakdown(record)}
              />
            </Tooltip>
          </Space>
        ),
      },
    );
    return visibleColumns;
  }, [handleDownloadBreakdown]);

  const headerButtons = (
    <Space size="middle" className="app-page-actions">
      <Button
        icon={<ReloadOutlined />}
        loading={isRescoring}
        disabled={isRescoring}
        onClick={handleRescore}
      >
        Re-score
      </Button>
      <Button
        type="primary"
        icon={<DownloadOutlined />}
        onClick={handleExport}
      >
        Export CSV
      </Button>
    </Space>
  );

  return (
    <div className="app-page">
      <div className="app-page-header">
        <Row align="middle" justify="space-between" wrap>
          <Col flex="1" style={{ minWidth: 0 }}>
            <div className="app-page-title-wrap">
              <Button
                type="text"
                icon={<ArrowLeftOutlined />}
                onClick={() => navigate(ROUTE_CANDIDATE_SCREENING)}
              />
              <div className="app-page-icon">
                <TrophyOutlined />
              </div>
              <h1 className="app-page-title">
                Ranking — {jd?.title || "Results"}
              </h1>
            </div>
            <p className="app-page-subtitle">
              Deterministic ranked results ({enabledScoreLabel || "configured"} blend)
            </p>
          </Col>
          <Col>{headerButtons}</Col>
        </Row>
      </div>

      {hasAiSkippedResults && (
        <div style={{ margin: "0 16px 16px" }}>
          <Alert
            type="warning"
            showIcon
            style={{ borderRadius: 6 }}
            message="AI scoring was skipped for one or more candidates"
            description="AI service is currently unavailable or not configured. Disabled score components were not used as fallback."
          />
        </div>
      )}

      <div className="app-table-card">
        <DataTableComponent
          pagination={true}
          loading={loadingResults}
          dataSource={resultsData}
          columns={columns}
          defaultPage={0}
          currentPage={resultsPagination.page}
          totalRows={resultsPagination?.total}
          onPageChange={onPageChange}
        />
      </div>

      {breakdownOpen && (
        <ScoreBreakdownComponent
          onClose={() => setBreakdownOpen(false)}
          visible={breakdownOpen}
          result={selectedResult}
        />
      )}
    </div>
  );
};

export default ScreeningResultsPage;
